import { useState, useEffect, useCallback } from 'react';
import { sortBy } from 'lodash';

import type { RegistryDuckType } from '../types/context-types';
import type { Item } from '../Gallery';

const defaultSortFields = ['name'];

/**
 * Determine if a warning message should be shown for the provided gallery items.
 * Returns the incoming warning message when no items are present, otherwise undefined.
 */
const getWarning = (items: Item[], message: string): string | undefined => {
    if (Array.isArray(items) && items.length) {
        return undefined;
    }

    return message;
};

export type ResourceRegistry<T> = RegistryDuckType<T>;

export type RegistryItemMapper<TRegistryItem extends { id: string }> = (
    entry: TRegistryItem & { providerType: string }
) => Item;

/**
 * Configuration for `useRegistryGallery`. Consumers provide the registry instance plus callbacks
 * describing how to convert registry entries into gallery items and which messages to surface.
 */
interface UseRegistryGalleryOptions<TRegistryItem extends { id: string }> {
    registry: RegistryDuckType<TRegistryItem> | null;
    initialProviderType?: string;
    missingRegistryMessage: string;
    missingProviderMessage: string;
    emptyWarningMessage: string;
    emptyDeleteWarningMessage?: string;
    emptySearchWarningMessage: string;
    mapItem: RegistryItemMapper<TRegistryItem>;
    listIdsOptions?: Record<string, unknown>;
    sortFields?: string[];
    disableInitialFetch?: boolean;
}

/**
 * Shape of the stateful values and callbacks returned from `useRegistryGallery`.
 */
interface UseRegistryGalleryResult {
    items: Item[];
    loading: boolean;
    searching: boolean;
    error?: string;
    warning?: string;
    providerType?: string;
    handleUpload: (files: File[]) => Promise<void>;
    handleSearch: (searchStr?: string) => Promise<void>;
    handleDelete: (item: Item) => Promise<void>;
}

/**
 * Shared hook that encapsulates all registry-backed Gallery behaviors such as
 * provider detection, listing/searching items, uploads, deletes, and warning/error state.
 * Consumers provide registry-specific mapping logic through the `mapItem` callback so UIs
 * can reuse the same data lifecycle without duplicating state management.
 */
export const useRegistryGallery = <TRegistryItem extends { id: string }>({
    registry,
    initialProviderType,
    missingRegistryMessage,
    missingProviderMessage,
    emptyWarningMessage,
    emptyDeleteWarningMessage,
    emptySearchWarningMessage,
    mapItem,
    listIdsOptions,
    sortFields = defaultSortFields,
    disableInitialFetch = false,
}: UseRegistryGalleryOptions<TRegistryItem>): UseRegistryGalleryResult => {
    const [items, setItems] = useState<Item[]>([]);
    const [providerType, setProviderType] = useState(initialProviderType);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string>();
    const [warning, setWarning] = useState<string>();
    const deleteWarningMessage =
        emptyDeleteWarningMessage ?? emptyWarningMessage;

    /**
     * Return the registry instance or throw when unavailable so downstream logic can rely on it.
     */
    const getRegistry = useCallback(() => {
        if (!registry) {
            throw new Error(missingRegistryMessage);
        }

        return registry;
    }, [registry, missingRegistryMessage]);

    useEffect(() => {
        if (!providerType) {
            try {
                const provider = getRegistry()
                    .listProviders()
                    .find((type) => type.isDefault);

                if (!provider) {
                    throw new Error(missingProviderMessage);
                }

                setProviderType(provider.type);
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message);
                }
            }
        }
    }, [providerType, getRegistry, missingProviderMessage]);

    /**
     * Fetch a flattened list of registry entries from every provider type, applying optional listId overrides.
     */
    const getItemsFromAllProviders = useCallback(
        async (
            registryInstance: RegistryDuckType<TRegistryItem>,
            options?: Record<string, unknown>
        ) => {
            const providers = registryInstance.listProviders();
            const itemLists = await Promise.all(
                providers.map(async (provider) => {
                    const ids = await registryInstance.listIds(provider.type, {
                        ...listIdsOptions,
                        ...options,
                    });
                    return ids.map((id) => ({
                        ...id,
                        providerType: provider.type,
                    }));
                })
            );

            return itemLists.reduce(
                (list, current) => list.concat(current),
                [] as Array<TRegistryItem & { providerType: string }>
            );
        },
        [listIdsOptions]
    );

    /**
     * Apply the consumer-provided mapper and sorting to registry items to produce `Gallery` items.
     */
    const buildItems = useCallback(
        (registryItems: Array<TRegistryItem & { providerType: string }>) =>
            sortBy(registryItems.map(mapItem), sortFields),
        [mapItem, sortFields]
    );

    /**
     * Refresh gallery items from the registry, updating warning/error state based on the results.
     */
    const fetchItems = useCallback(async () => {
        if (!providerType) {
            setLoading(false);
            return;
        }

        try {
            const registryInstance = getRegistry();
            const registryItems =
                await getItemsFromAllProviders(registryInstance);
            const newItems = buildItems(registryItems);

            setItems(newItems);
            setError(undefined);
            setWarning(getWarning(newItems, emptyWarningMessage));
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, [
        getRegistry,
        providerType,
        getItemsFromAllProviders,
        buildItems,
        emptyWarningMessage,
    ]);

    useEffect(() => {
        if (disableInitialFetch) {
            setLoading(false);
            return;
        }
        fetchItems();
    }, [fetchItems, disableInitialFetch]);

    /**
     * Convert a File object into a Data URL so it can be uploaded through the registry API.
     */
    const readFileAsDataURL = useCallback((file: File) => {
        return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.onload = () => resolve(fileReader.result);
            fileReader.onerror = () =>
                reject(
                    new Error(fileReader.error?.message ?? 'File reading error')
                );
            fileReader.readAsDataURL(file);
        });
    }, []);

    /**
     * Upload a single file to the registry, returning the gallery item fields used in state.
     */
    const uploadFile = useCallback(
        async (
            registryInstance: RegistryDuckType<TRegistryItem>,
            file: File,
            uploadProviderType: string
        ) => {
            const fileName = file.name;
            const contents = await readFileAsDataURL(file);
            const url = await registryInstance.upload(
                contents,
                { name: fileName },
                uploadProviderType
            );

            return { url, name: fileName };
        },
        [readFileAsDataURL]
    );

    /**
     * Upload one or many files, merging successful uploads into the gallery state and reporting errors.
     */
    const handleUpload = useCallback(
        async (files: File[]) => {
            if (!providerType) {
                return;
            }

            setLoading(true);
            try {
                const registryInstance = getRegistry();
                const uploadedItems: Item[] = [];
                const uploadErrors: string[] = [];

                const results = await Promise.allSettled(
                    files.map((file) =>
                        uploadFile(registryInstance, file, providerType)
                    )
                );

                results.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        uploadedItems.push(result.value);
                    } else {
                        uploadErrors.push(
                            result.reason instanceof Error
                                ? result.reason.message
                                : String(result.reason)
                        );
                    }
                });

                if (uploadedItems.length) {
                    setItems((currentItems) =>
                        sortBy([...currentItems, ...uploadedItems], sortFields)
                    );
                    setWarning(undefined);
                }

                if (uploadErrors.length) {
                    setError(uploadErrors.join(', '));
                } else {
                    setError(undefined);
                }
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        },
        [providerType, uploadFile, sortFields, getRegistry]
    );

    /**
     * Execute a registry search and update the gallery with the filtered results.
     */
    const handleSearch = useCallback(
        async (searchStr = '') => {
            setSearching(true);

            try {
                const registryInstance = getRegistry();
                const registryItems = await getItemsFromAllProviders(
                    registryInstance,
                    {
                        search: searchStr.trim(),
                    }
                );
                const newItems = buildItems(registryItems);

                setItems(newItems);
                setError(undefined);
                setWarning(getWarning(newItems, emptySearchWarningMessage));
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message);
                }
            } finally {
                setSearching(false);
            }
        },
        [
            getRegistry,
            getItemsFromAllProviders,
            buildItems,
            emptySearchWarningMessage,
        ]
    );

    /**
     * Delete an item from the registry and reconcile local state, updating warnings/errors as needed.
     */
    const handleDelete = useCallback(
        async (item: Item) => {
            setLoading(true);

            try {
                await getRegistry().deleteByURL(item.url);
                const newItems = items.filter(
                    (registryItem) => registryItem.url !== item.url
                );
                setItems(newItems);
                setWarning(getWarning(newItems, deleteWarningMessage));
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        },
        [items, getRegistry, deleteWarningMessage]
    );

    return {
        items,
        loading,
        searching,
        error,
        warning,
        providerType,
        handleUpload,
        handleSearch,
        handleDelete,
    };
};
